import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import PerfectScrollbar from "perfect-scrollbar";
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import Footer from "components/footer/Footer";
import Sidebar from "components/sidebar/Sidebar";
import FixedPlugin from "components/FixedPlugin/FixedPlugin.js";
import routes from "routes.js";
import logo from "black-white-logo.png";
import { BackgroundColorContext } from "contexts/BackgroundColorContext";
import PrivateRoute from "views/PrivateRoute";
import { useAuth } from "views/AuthContext";
import { requestFirebaseNotificationPermission } from "./Firebase";
import { useTranslation } from "../../hooks/useTranslation";
import { useFetchCompanyUserDataQuery } from "../../api/usersApi";

var ps;

// ── Translation map (mirrors Sidebar.js — used for navbar brand text) ─────────
const ROUTE_TRANSLATION_MAP = {
  "/dashboard":           "navigation.dashboard",
  "/analytics":           "navigation.analytics",
  "/analytics/sales": "navigation.analytics",
  "/analytics/products": "navigation.analytics",
  "/analytics/inventory": "navigation.analytics",
  "/analytics/profitability": "navigation.analytics",
  "/analytics/insights": "navigation.analytics",
  "/analytics/reports": "navigation.analytics",
  "/sales":               "navigation.sales",
  "/sales/history":       "navigation.recentSales",
  "/returns":             "navigation.returns",
  "/returns/history":     "navigation.recentReturns",
  "/exchanges":           "navigation.exchanges",
  "/exchanges/history":   "navigation.recentExchanges",
  "/orders":              "navigation.orders",
  "/orders/create":       "navigation.createOrder",
  "/customers":           "navigation.customers",
  "/catalog/products":    "navigation.products",
  "/catalog/collections": "navigation.collections",
  "/marketing/meta-ads":  "navigation.metaAds",
  "/stock-check":         "navigation.stockCheck",
  "/transactions":        "navigation.transactions",
  "/cms":                 "navigation.referenceData",
  "/settings":            "navigation.administration",
  "/add/products":        "navigation.legacyIntake",
  "/products":            "navigation.legacyProducts",
  "/icons":               "navigation.icons",
  "/notifications":       "navigation.notifications",
  "/user-profile":        "navigation.userProfile",
  "/company-profile":     "navigation.companyProfile",
  "/login":               "navigation.login",
  "/tables":              "navigation.tableList",
};

function Admin(props) {
  const t = useTranslation();
  const location = useLocation();
  const { token } = useAuth();
  const mainPanelRef = React.useRef(null);
  const [sidebarOpened, setSidebarOpened] = React.useState(
    document.documentElement.className.indexOf("nav-open") !== -1
  );

  // ── Current user role (for role-based sidebar filtering) ─────────────────
  // Only fetch when the user is authenticated. The Sidebar consumes `userRole`
  // and shows only safe/common routes while this is still loading (null).
  const { data: companyUser } = useFetchCompanyUserDataQuery(undefined, {
    skip: !token,
  });
  const userRole = token
    ? (String(companyUser?.user?.role || "").toLowerCase() || null)
    : null;

  const getRouteTranslationKey = (path) => ROUTE_TRANSLATION_MAP[path] || null;

  React.useEffect(() => {
    if (!token) {
      return;
    }

    requestFirebaseNotificationPermission();
  }, [token]);

  React.useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1) {
      document.documentElement.className += " perfect-scrollbar-on";
      document.documentElement.classList.remove("perfect-scrollbar-off");
      ps = new PerfectScrollbar(mainPanelRef.current, {
        suppressScrollX: true,
      });
      let tables = document.querySelectorAll(".table-responsive");
      for (let i = 0; i < tables.length; i++) {
        ps = new PerfectScrollbar(tables[i]);
      }
    }

    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1) {
        ps.destroy();
        document.documentElement.classList.add("perfect-scrollbar-off");
        document.documentElement.classList.remove("perfect-scrollbar-on");
      }
    };
  });

  React.useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1) {
      let tables = document.querySelectorAll(".table-responsive");
      for (let i = 0; i < tables.length; i++) {
        ps = new PerfectScrollbar(tables[i]);
      }
    }
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainPanelRef.current) {
      mainPanelRef.current.scrollTop = 0;
    }
  }, [location]);

  const toggleSidebar = () => {
    document.documentElement.classList.toggle("nav-open");
    setSidebarOpened((prev) => !prev);
  };

  React.useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 990px)").matches;
    if (isMobile) {
      document.documentElement.classList.remove("nav-open");
      setSidebarOpened(false);
    }
  }, [location.pathname]);

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/admin") {
        if (prop.authentication) {
          return (
            <Route
              key={key}
              path={prop.path}
              element={<PrivateRoute element={prop.component} roles={prop.roles} />}
              exact
            />
          );
        } else {
          return (
            <Route path={prop.path} element={prop.component} key={key} exact />
          );
        }
      } else {
        return null;
      }
    });
  };

  const getBrandText = (path) => {
    if (path.startsWith("/admin/catalog/products/")) {
      return "Product Studio";
    }

    const matchedRoute = routes
      .filter((route) => location.pathname.indexOf(route.layout + route.path) !== -1)
      .sort((left, right) => right.path.length - left.path.length)[0];

    if (matchedRoute) {
      const translationKey = getRouteTranslationKey(matchedRoute.path);
      if (translationKey) {
        return t(translationKey);
      }
      return matchedRoute.name;
    }
    return "Brand";
  };

  const getBrandIcon = (path) => {
    if (path.startsWith("/admin/catalog/products/")) {
      return "tim-icons icon-atom";
    }

    const matchedRoute = routes
      .filter((route) => location.pathname.indexOf(route.layout + route.path) !== -1)
      .sort((left, right) => right.path.length - left.path.length)[0];

    return matchedRoute?.icon || null;
  };

  return (
    <BackgroundColorContext.Consumer>
      {({ color, changeColor }) => (
        <React.Fragment>
          <div className="wrapper">
            {token && (
              <Sidebar
                routes={routes}
                userRole={userRole}
                logo={{
                  outterLink: "/",
                  text: "Black&White CMS",
                  imgSrc: logo,
                }}
                toggleSidebar={toggleSidebar}
              />
            )}
            <div className="main-panel" ref={mainPanelRef} data={color}>
              {token && (
                <AdminNavbar
                  brandText={getBrandText(location.pathname)}
                  brandIcon={getBrandIcon(location.pathname)}
                  toggleSidebar={toggleSidebar}
                  sidebarOpened={sidebarOpened}
                />
              )}

              <Routes>
                {getRoutes(routes)}
                <Route
                  path="/"
                  element={<Navigate to="/admin/dashboard" replace />}
                />
              </Routes>
              {location.pathname === "/admin/maps" ? null : <Footer fluid />}
            </div>
          </div>
          <FixedPlugin bgColor={color} handleBgClick={changeColor} />
        </React.Fragment>
      )}
    </BackgroundColorContext.Consumer>
  );
}

export default Admin;
